import {
  FilterCategory,
  IndexedFilterValue,
  IndexedFilters,
} from '@/catalog/types/filters';
import {
  filterIndexedNestedFilter,
  filterNumberArray,
  filterNumberRange,
  filterStringArray,
  isTypeIndexedNestedFilter,
  isTypeNumberArray,
  isTypeNumberRange,
  isTypeStringArray,
} from '@/catalog/utils/filter-menu-type-guards';
import React from 'react';
import FilterOptionCheckboxGroup from './filter-option-checkbox-group/filter-option-checkbox-group';
import FilterOptionNestedCheckboxGroup from './filter-option-nested-checkbox-group/filter-option-nested-checkbox-group';
import { NumberObject } from '@/common/types/number-object';
import { NestedFilterCounts } from '@/catalog/types/catalog-market-loader';
import FilterOptionSlider from './filter-option-slider/filter-option-slider';

/**
 * Get filter note for filter, if applicable.
 *
 * @param categoryName Name of filter category
 * @param selectedPlanName Name of selected plan
 * @returns
 */
function filterFooterNote(
  categoryName: string,
  selectedPlanName: string
): string | React.ReactNode | undefined {
  switch (categoryName) {
    case 'miles_per_gallon':
      return 'This is an average miles per gallon of city and highway combined';
    case 'monthly_price':
      return (
        <span>
          Price is based on{' '}
          {selectedPlanName ? (
            <span className="body3-strong">{selectedPlanName}</span>
          ) : (
            'your selected plan'
          )}
          . You can change your monthly plan at a later step.
        </span>
      );
    default:
      return undefined;
  }
}

/**
 * Determine if filter count is for nested or single level filters
 * @param count Filter count object
 * @returns boolean
 */
function isNestedFilterCount(
  count?:
    | NumberObject
    | {
        [key: string]: NestedFilterCounts;
      }
): count is {
  [key: string]: NestedFilterCounts;
} {
  if (!count) return false;
  return Object.values(count).some(
    (value) => typeof value === 'object' && 'count' in value
  );
}

interface FilterMenuCategoryProps {
  /* Name of this filter category. The value is later converted from camelCase to a human-readable title. **/
  categoryName: FilterCategory;
  /* Available filters for this category. **/
  categoryFilters: IndexedFilterValue;
  categoryFilterCount?:
    | {
        [key: string]: NestedFilterCounts;
      }
    | NumberObject;
  /* Selected filters for this category. **/
  selectedCategoryFilters?: IndexedFilterValue;
  setSelectedFilters: React.Dispatch<React.SetStateAction<IndexedFilters>>;
  /* Selected plan name */
  selectedPlanName?: string;
  /* Flag to indicate if filter should show footer note */
  canViewFooterNote?: boolean;
}

/**
 * Component that traverses each filter category, determines it's type,
 * and renders the appropriate component based on the filter type.
 */
function FilterMenuCategory({
  categoryName,
  categoryFilters,
  categoryFilterCount,
  selectedCategoryFilters,
  setSelectedFilters,
  selectedPlanName,
  canViewFooterNote,
}: FilterMenuCategoryProps) {
  if (isTypeIndexedNestedFilter(categoryFilters)) {
    const filteredSelected = filterIndexedNestedFilter(selectedCategoryFilters);
    const nestedCheckboxGroupFilterCount = isNestedFilterCount(categoryFilterCount)
      ? categoryFilterCount
      : undefined;

    return (
      <FilterOptionNestedCheckboxGroup
        categoryName={categoryName}
        categoryFilters={categoryFilters}
        categoryFilterCount={nestedCheckboxGroupFilterCount}
        selectedFilters={filteredSelected}
        setSelectedFilters={setSelectedFilters}
      />
    );
  }

  if (isTypeStringArray(categoryFilters)) {
    const filteredSelected = filterStringArray(selectedCategoryFilters);
    const checkBoxGroupFilterCount = !isNestedFilterCount(categoryFilterCount)
      ? categoryFilterCount
      : undefined;

    return (
      <FilterOptionCheckboxGroup
        categoryName={categoryName}
        categoryFilters={categoryFilters}
        categoryFiltersCount={checkBoxGroupFilterCount}
        selectedFilters={filteredSelected}
        setSelectedFilters={setSelectedFilters}
      />
    );
  }

  if (isTypeNumberArray(categoryFilters)) {
    const filteredSelected = filterNumberArray(selectedCategoryFilters);

    return (
      <FilterOptionSlider
        categoryName={categoryName}
        filters={categoryFilters}
        selectedFilters={filteredSelected}
        setSelectedFilters={setSelectedFilters}
        prefixVariant="none"
        allowThumbOverlap={true}
        disableThumbLabels={true}
        showEdgeLabels={true}
      />
    );
  }

  if (isTypeNumberRange(categoryFilters)) {
    const filteredSelected = filterNumberRange(selectedCategoryFilters);

    return (
      <FilterOptionSlider
        categoryName={categoryName}
        filters={categoryFilters}
        selectedFilters={filteredSelected}
        setSelectedFilters={setSelectedFilters}
        prefixVariant={categoryName === 'monthly_price' ? 'dollar' : 'none'}
        allowThumbOverlap={false}
        step={categoryName === 'mileage' ? 10000 : 1}
        footerNote={
          canViewFooterNote
            ? filterFooterNote(categoryName, selectedPlanName ?? '')
            : undefined
        }
        disableThumbLabels={true}
        showEdgeLabels={true}
      />
    );
  }

  return <React.Fragment />;
}

// Note: Without this, all FilterMenuCategories re-render if another is toggled.
export default React.memo(FilterMenuCategory);
